import { useQuery } from '@tanstack/react-query';
import { getCollectionsForPlace, getImage } from '../api';
import { CollectionContentShape, CollectionShape } from '../types';
import { filterCollections, FilterCollections } from '../utils';

const FIVE_MINUTES = 5 * 60 * 1000;

export const transformEditorialCollection = (
    collection: CollectionShape,
): CollectionContentShape => ({
    displayCreatorName: collection.display_creator_name,
    displayCreatorImageId: collection.display_creator_image_id,
    creatorImageIdOverrideUrl: collection.creator_image_id_override_url,
    eventCount: collection.event_count,
    id: collection.id,
    imageLarge: {
        url: collection?.image_large?.url,
    },
    name: collection.name,
    summary: collection.summary,
});

/**
 * Util that fetches Creator Images for any collections
 * that have custom image overrides defined
 * but no associated image URL.
 *
 * @param collections
 * @returns CollectionContentShape[]
 */
export async function fetchAndUpdateCollectionCreatorImages(
    collections: CollectionContentShape[],
): Promise<CollectionContentShape[]> {
    const externalCollections = collections.filter(
        (collection) =>
            typeof collection.displayCreatorImageId === 'string' &&
            !collection.creatorImageIdOverrideUrl &&
            collection.displayCreatorName !== 'Eventbrite',
    );

    if (externalCollections.length === 0) {
        return collections;
    }

    //Fetch associated images via Promise.all to make it as sequential
    //as possible instead of a synchronous action. There is no api v3
    //option to fetch multiple images at once.
    const newCollections = [...collections];
    const promises = externalCollections.map((collection) =>
        getImage(collection.displayCreatorImageId as string),
    );

    try {
        const resolved = await Promise.all(promises);

        //Update associated collections with
        //the returned image data.
        resolved.map((imageUrl, index) => {
            const customCreatorCollection = externalCollections[index];
            const collectionIndexToUpdate = collections.findIndex(
                (collection) => collection.id === customCreatorCollection.id,
            );
            newCollections[collectionIndexToUpdate] = {
                ...newCollections[collectionIndexToUpdate],
                creatorImageIdOverrideUrl: imageUrl,
            };
        });

        return newCollections;
    } catch (e) {
        console.warn(
            'Error fetching and assigning creator images to collections',
            e,
        );
    }

    return newCollections;
}

type CollectionFilteringConfig = Pick<
    FilterCollections,
    'tld' | 'collectionBlockList' | 'renderCollectionsWithZeroEvents'
>;

export const useFeaturedCollectionsForPlace = (
    place: string | string[],
    collectionFilteringConfig: CollectionFilteringConfig,
    initialData?: CollectionContentShape[],
) => {
    const response = useQuery<CollectionContentShape[], Error>(
        ['featured-collections', { place }, collectionFilteringConfig.tld],
        async () => {
            const collectionResponse = await getCollectionsForPlace(place);
            const formattedCollectionResponse =
                collectionResponse.collections.map(
                    transformEditorialCollection,
                );

            //We filter out collections that should not be rendered on current
            //tld and sort by priority as defined in the config files. We do this
            //here so that the result is cached and this logic is not currently
            //handled via the backend.
            const filteredCollections = filterCollections({
                collections: formattedCollectionResponse,
                tld: collectionFilteringConfig.tld,
                collectionBlockList:
                    collectionFilteringConfig?.collectionBlockList,
                renderCollectionsWithZeroEvents:
                    collectionFilteringConfig?.renderCollectionsWithZeroEvents,
            });

            const updatedCollections =
                await fetchAndUpdateCollectionCreatorImages(
                    filteredCollections,
                );

            return updatedCollections;
        },
        {
            initialData,
            enabled: !!place,
            retry: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
            staleTime: FIVE_MINUTES,
        },
    );
    return response;
};
